import React, { useState } from 'react';
import { Checkbox, Collapse, TableCell } from '@mui/material';
import MUITableRow from '@mui/material/TableRow';

import { TableColumnType } from '../@types';

import { TableRowProps } from './props';

export const TableRow = <T,>(props: TableRowProps<T>) => {
  const {
    collapsible,
    collapsibleColSpan,
    columns = [],
    data,
    checked = false,
    onChecked,
    onClick,
    onExpand,
    renderExpandView,
    ...restProps
  } = props;
  // ================== STATE
  const [expanded, setExpanded] = useState(false);

  // ======================= EVENTS
  const onHandleClickRow = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(data, e);
    if (!collapsible) return;
    setExpanded(!expanded);
  };

  // ======================= RENDER FUNCTIONS
  const renderColumnData = (column: TableColumnType<T>, index: number) => {
    const { key, type, dataIndex, render } = column;
    const result = dataIndex ? data?.[dataIndex] : data;

    if (render) {
      return render(result, data, index);
    }

    if (type === 'checkbox') {
      const id = `checkbox-${key}-${index}`;
      return (
        <Checkbox
          color="primary"
          checked={checked}
          inputProps={{ 'aria-labelledby': id }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const checkedState = event.target.checked;
            onChecked?.(result, checkedState, {
              index,
              dataIndex: dataIndex ?? 0,
              data,
            });
          }}
        />
      );
    }

    if (typeof result === 'object') {
      // return JSON.stringify(result);
      return;
    }
    return result;
  };

  // ======================= VIEWS
  return (
    <React.Fragment>
      <MUITableRow onClick={onHandleClickRow} {...restProps}>
        {columns.map((column, index) => {
          const id =
            column.type === 'checkbox'
              ? `checkbox-${column.key}-${index}`
              : undefined;
          return (
            <TableCell
              id={id}
              padding={column.type === 'checkbox' ? 'checkbox' : undefined}
              key={`${column.key}-${index}`}
              align={column.align ?? 'left'}
              {...column.dataCellProps}
              sx={{
                width: column.width,
                ...column.dataCellProps?.sx,
              }}
            >
              <>{renderColumnData(column, index)}</>
            </TableCell>
          );
        })}
      </MUITableRow>
      {collapsible && (
        <MUITableRow {...restProps}>
          <TableCell
            style={{ padding: 0 }}
            colSpan={collapsibleColSpan ?? columns.length}
          >
            <Collapse
              timeout="auto"
              in={expanded}
              unmountOnExit
              addEndListener={() => onExpand?.(data, expanded)}
            >
              {renderExpandView?.(data, expanded)}
            </Collapse>
          </TableCell>
        </MUITableRow>
      )}
    </React.Fragment>
  );
};

// ======================= EXPORT
export * from './props';
export default TableRow;
